
import tidalIcon from '@tidal/styles-icons';
import { sidebarHome, socialFacebook, socialInstagram, socialTwitter, detailViewInfo } from '@tidal/styles-icons';
import footerLink from './tidal-footer-link.vue';

const FALLBACK_TRANSLATIONS = {
    about: 'About',
    description: 'TIDAL is an artist-first, fan-centered music streaming platform that delivers over 100 million songs in HiFi sound quality to the global music community.',
    account: 'Account',
    career: 'Careers',
    company: 'Company',
    contact: 'Contact',
    cookiesSettings: 'Cookies Settings',
    slaveryStatement: 'Modern Slavery Statement',
    discoverTidal: 'Discover TIDAL',
    download: 'Download TIDAL',
    getStarted: 'Get Started',
    getSupport: 'Get Support',
    magazine: 'Magazine',
    manageAccount: 'Manage Account',
    partners: 'Partners',
    transferMusic: 'Transfer Music',
    press: 'Press',
    privacyPolicy: 'Privacy policy',
    redeemGiftcard: 'Redeem Giftcard',
    redeemVoucher: 'Redeem Voucher',
    signUp: 'Sign Up',
    startFreeTrial: 'Start Free Trial',
    getOffer: 'Get Offer',
    supportedDevices: 'Supported Devices',
    termsConditions: 'Terms and Conditions',
    accessibility: 'Accessibility Statement',
    whatIsTidal: 'What is TIDAL?',
    soundQuality: 'Sound Quality',
    pricingPlans: 'Pricing & Plans',
    exploreApp: 'Explore the App',
    culture: 'Culture',
    forMusic: 'For Music',
    forArtists: 'For Artists',
    videoNotAvailable: 'Video content is not available in your market',
    digitalServicesAct: 'DSA',
};

export default {
    components: {
        tidalIcon,
        footerLink,
    },
    props: {
        baseUrl: {
            type: String,
            default: '/',
        },
        tidalUrl: {
            type: String,
            default: null,
        },
        country: {
            type: String,
            default: null,
        },
        signupUrl: {
            type: String,
            default: 'try-now',
        },
        minimal: {
            type: Boolean,
            default: false,
        },
        fullwidth: {
            type: Boolean,
            default: false,
        },
        transparent: {
            type: Boolean,
            default: false,
        },
        disableLine: {
            type: Boolean,
            default: false,
        },
        videosDisclaimer: {
            type: Boolean,
            default: false,
        },
        disableClickableLogo: {
            type: Boolean,
            default: false,
        },
        /**
         * @property {string} [translations[].about]
         * @property {string} [translations[].description]
         * @property {string} [translations[].account]
         * @property {string} [translations[].career]
         * @property {string} [translations[].company]
         * @property {string} [translations[].contact]
         * @property {string} [translations[].cookiesSettings]
         * @property {string} [translations[].slaveryStatement]
         * @property {string} [translations[].discoverTidal]
         * @property {string} [translations[].download]
         * @property {string} [translations[].getStarted]
         * @property {string} [translations[].getSupport]
         * @property {string} [translations[].magazine]
         * @property {string} [translations[].manageAccount]
         * @property {string} [translations[].partners]
         * @property {string} [translations[].transferMusic]
         * @property {string} [translations[].press]
         * @property {string} [translations[].privacyPolicy]
         * @property {string} [translations[].redeemGiftcard]
         * @property {string} [translations[].redeemVoucher]
         * @property {string} [translations[].signUp]
         * @property {string} [translations[].startFreeTrial]
         * @property {string} [translations[].supportedDevices]
         * @property {string} [translations[].termsConditions]
         * @property {string} [translations[].accessibility]
         * @property {string} [translations[].whatIsTidal]
         * @property {string} [translations[].soundQuality]
         * @property {string} [translations[].pricingPlans]
         * @property {string} [translations[].exploreApp]
         * @property {string} [translations[].culture]
         * @property {string} [translations[].forMusic]
         * @property {string} [translations[].forArtists]
         * @property {string} [translations[].videoNotAvailable]
         * @property {string} [translations[].digitalServicesAct]
         * @type {import('vue').PropOptions<typeof FALLBACK_TRANSLATIONS>}
         */
        translations: {
            type: Object,
            default: null,
        },
        translationFn: {
            type: Function,
            default: null,
        },
        nuxtLinks: {
            type: Array,
            default: () => [],
        },
        privacyUrl: {
            type: String,
            default: 'privacy',
        },
        termsUrl: {
            type: String,
            default: 'terms',
        },
        campaignId: {
            type: String,
            default: null,
        },
        contactUrl: {
            type: String,
            default: 'contact',
        },
        showCookieSettings: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        /** @return {boolean} */
        isBrazil() {
            return !!this.country && this.country.toLowerCase() === 'br';
        },
        /** @return {boolean} */
        isUK() {
            return !!this.country && this.country.toLowerCase() === 'gb';
        },
        /** @return {boolean} */
        isEU() {
            const euCountries = [
                'be', 'bg', 'cz', 'dk', 'de', 'ee', 'ie', 'gr', 'es', 'fr', 'hr', 'it', 'cy', 'lv', 'lt', 'lu', 'hu', 'mt', 'nl', 'at', 'pl', 'pt', 'ro', 'si', 'sk', 'fi', 'se',
            ];
            return !!this.country && euCountries.includes(this.country.toLowerCase());
        },
        /** @return {boolean} */
        _videosDisclaimer() {
            return this.videosDisclaimer || (this.country && this.country.toLowerCase()) === 'ae';
        },
        /** @return {string} */
        deviceImage() {
            return '<svg viewBox="0 0 300 153" fill="none" class="block fullwidth padding-left-nudge padding-right-nudge margin-bottom-2"><path d="M55.5561798,83.6335178 L82.7174952,83.7716499 C85.1938121,83.7842435 87.1946103,85.7952428 87.1946103,88.2715917 L87.1946103,130.960674 L172.410017,130.960674 L172.519072,64.6597133 C172.523144,62.1838722 174.526462,60.1763765 177.00229,60.1671464 L245.567416,59.9115285 L245.567416,5 C245.567416,2.51471863 243.552697,0.5 241.067416,0.5 L60.0561798,0.5 C57.5708984,0.5 55.5561798,2.51471863 55.5561798,5 C55.5561798,13.3216841 55.5561798,13.3216841 55.5561798,21.6433682 C55.5561798,29.9650522 55.5561798,29.9650522 55.5561798,38.2867363 C55.5561798,49.2374314 55.5561798,49.2374314 55.5561798,60.5964194 C55.5561798,71.9971276 55.5561798,72.1572724 55.5561798,83.6335178 Z" stroke="#9B9B9B"></path><path d="M94.3820225,138.202247 L171.910112,138.202247" stroke="#9B9B9B" stroke-linecap="square"></path><rect stroke="#979797" x="165.668539" y="146.567416" width="133.831461" height="5.74157303" rx="2"></rect><path d="M176.910112,60.0505618 C174.424831,60.0505618 172.410112,62.0652804 172.410112,64.5505618 C172.410112,69.3544475 172.410112,69.3544475 172.410112,74.1583331 C172.410112,78.9622188 172.410112,78.9622188 172.410112,83.7661044 C172.410112,97.4891474 172.410112,97.4891474 172.410112,111.21219 L172.410112,137.696629 C172.410112,140.181911 174.424831,142.196629 176.910112,142.196629 L288.258427,142.196629 C290.743708,142.196629 292.758427,140.181911 292.758427,137.696629 L292.758427,64.5505618 C292.758427,62.0652804 290.743708,60.0505618 288.258427,60.0505618 L176.910112,60.0505618 Z" stroke="#9B9B9B"></path><path d="M42.0786517,83.6460674 C39.5933703,83.6460674 37.5786517,85.660786 37.5786517,88.1460674 C37.5786517,95.7904164 37.5786517,95.7904164 37.5786517,103.434765 C37.5786517,114.93245 37.5786517,114.93245 37.5786517,126.430135 L37.5786517,147.808989 C37.5786517,150.29427 39.5933703,152.308989 42.0786517,152.308989 L82.6404494,152.308989 C85.1257308,152.308989 87.1404494,150.29427 87.1404494,147.808989 L87.1404494,88.1460674 C87.1404494,85.660786 85.1257308,83.6460674 82.6404494,83.6460674 L42.0786517,83.6460674 Z" stroke="#9B9B9B"></path><path d="M5,100.5 C2.51471863,100.5 0.5,102.514719 0.5,105 C0.5,110.190071 0.5,110.190071 0.5,115.380142 C0.5,119.738135 0.5,119.738135 0.5,124.096129 C0.5,128.454122 0.5,128.454122 0.5,132.812115 L0.5,147.808989 C0.5,150.29427 2.51471863,152.308989 5,152.308989 L21.9662921,152.308989 C24.4515735,152.308989 26.4662921,150.29427 26.4662921,147.808989 L26.4662921,105 C26.4662921,102.514719 24.4515735,100.5 21.9662921,100.5 L5,100.5 Z" stroke="#9B9B9B"></path></svg>';
        },
        /** @return {number} */
        currentYear() {
            const today = new Date();
            return today.getFullYear();
        },
        icons() {
            return {
                sidebarHome,
                detailViewInfo,
            };
        },
        socialLinks() {
            let twitter = 'https://twitter.com/TIDAL';
            let instagram = 'https://instagram.com/tidal';
            let facebook = 'https://facebook.com/TIDAL';
            if (this.country) {
                switch (this.country.toLowerCase()) {
                    case 'de':
                    case 'at':
                        instagram = 'https://instagram.com/tidal_germany/';
                        facebook = 'https://facebook.com/TIDALDeutschland/';
                        break;
                    case 'ch':
                        facebook = 'https://facebook.com/TIDALSwitzerland/';
                        break;
                    case 'pl':
                        instagram = 'https://instagram.com/tidal_polska/';
                        facebook = 'https://facebook.com/TIDALPolska';
                        break;
                    case 'bg':
                        instagram = 'https://instagram.com/tidal_bulgaria/';
                        facebook = 'https://facebook.com/TIDALBulgaria';
                        break;
                    case 'br':
                        twitter = 'https://twitter.com/Tidal_Brasil';
                        facebook = 'https://facebook.com/TIDALBRASIL/';
                        instagram = 'https://instagram.com/tidal_brasil/';
                        break;
                    case 'es':
                        instagram = 'https://instagram.com/tidal_spain/';
                        facebook = 'https://facebook.com/TIDALEspana/';
                        break;
                    case 'it':
                        instagram = 'https://instagram.com/tidal.italia';
                        facebook = 'https://facebook.com/TIDALItalia';
                        break;
                    case 'no':
                        instagram = 'https://instagram.com/tidal_norway';
                        facebook = 'https://facebook.com/TIDALNorge';
                        break;
                    case 'pt':
                        instagram = 'https://instagram.com/tidal_portugal';
                        break;
                    case 'dk':
                        facebook = 'https://facebook.com/TIDALDanmark';
                        break;
                }
            }

            return [
                {
                    title: 'Twitter',
                    href: twitter,
                    icon: socialTwitter,
                }, {
                    title: 'Instagram',
                    href: instagram,
                    icon: socialInstagram,
                }, {
                    title: 'Facebook',
                    href: facebook,
                    icon: socialFacebook,
                }, {
                    title: 'YouTube',
                    href: 'https://youtube.com/tidal',
                    icon: '<svg width="35" height="25" viewBox="0 0 35 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M33.3523 4.0823C32.9613 2.60746 31.8058 1.44617 30.3406 1.05133C27.6852 0.335205 17.0323 0.335205 17.0323 0.335205C17.0323 0.335205 6.37936 0.335205 3.72387 1.05133C2.25871 1.44617 1.10323 2.60746 0.712258 4.0823C0 6.75714 0 12.3352 0 12.3352C0 12.3352 0 17.9133 0.712258 20.5881C1.10323 22.0629 2.25871 23.2242 3.72387 23.6191C6.37936 24.3352 17.0323 24.3352 17.0323 24.3352C17.0323 24.3352 27.6852 24.3352 30.3406 23.6191C31.8058 23.2242 32.9613 22.0629 33.3523 20.5881C34.0645 17.9133 34.0645 12.3352 34.0645 12.3352C34.0645 12.3352 34.0645 6.75714 33.3523 4.0823ZM13.5484 17.4004V7.27004L22.4516 12.3352L13.5484 17.4004Z" fill="currentColor"/></svg>',
                    large: true,
                }, {
                    title: 'TikTok',
                    href: 'https://tiktok.com/@tidal',
                    icon: '<svg width="23" height="25" viewBox="0 0 23 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.001 8.66986C12.001 5.84602 12.001 3.02149 12.001 0.197644C12.001 0.00100366 12.001 0.000308819 12.1931 0.000308819C13.4767 0.000308819 14.7596 -0.000386024 16.0432 0.000308819C16.2539 0.000308819 16.2477 0.00169851 16.26 0.218489C16.342 1.70823 17.0619 3.0868 18.1163 4.11239C19.1074 5.07614 20.5879 5.97804 21.9968 5.96415C22.1276 5.96276 22.1668 6.00584 22.1675 6.13716C22.1778 7.40733 22.1936 8.6782 22.2081 9.94837C22.2081 9.96088 22.2081 9.97339 22.2081 9.9859C22.2067 10.2611 22.2067 10.2631 21.9417 10.2611C20.0862 10.2458 18.3799 9.71491 16.801 8.74421C16.695 8.6789 16.5945 8.60455 16.4872 8.54132C16.4438 8.51561 16.3888 8.51005 16.3392 8.49546C16.3261 8.54132 16.302 8.58648 16.302 8.63234C16.3 9.17293 16.3034 9.71421 16.3027 10.2548C16.3013 12.5881 16.3199 14.9221 16.291 17.2553C16.2683 19.1043 15.6372 20.7407 14.4485 22.1526C12.6879 24.2441 9.93827 25.3329 7.23474 24.9097C3.70046 24.3559 0.913649 21.2653 0.641094 17.682C0.541983 16.3833 0.750529 15.0582 1.27637 13.8666C2.65498 10.7398 5.9869 8.87971 9.32915 9.21045C9.45717 9.22296 9.49158 9.28202 9.49227 9.4064C9.50053 10.721 9.51429 12.035 9.52531 13.3496C9.52737 13.6199 9.51085 13.6324 9.24793 13.5643C6.78667 12.9313 4.45412 15.1993 4.92628 17.6987C5.2649 19.4913 7.01587 20.8032 8.8157 20.6232C10.6382 20.4405 11.9886 18.8299 11.9969 17.0115C12.0079 14.2307 12.0003 11.4499 12.001 8.66986Z" fill="currentColor"/></svg>',
                },
            ];
        },
        fallbackTranslations() {
            return FALLBACK_TRANSLATIONS;
        },
        _signupUrl() {
            if (this.campaignId) {
                return this.signupUrl + '?campaignId=' + this.campaignId;
            }

            return this.signupUrl;
        },
        /**
         * @return {Boolean}
         */
        hideAspiro() {
            const currentUnixTimestamp = Math.floor(Date.now() / 1000);
            return currentUnixTimestamp >= 1704085200;
        },
    },
    methods: {
        openCookieSettings() {
            if (window.Optanon?.ToggleInfoDisplay) {
                window.Optanon.ToggleInfoDisplay();
            }
        },
        /**
         * @param {keyof FALLBACK_TRANSLATIONS} translationKey
         * @return {string}
         */
        getTranslation(translationKey) {
            let translation;

            if (this.translationFn) {
                translation = this.translationFn(translationKey);
            }

            if (!translation && this.translations) {
                translation = this.translations[translationKey];
            }

            const isTranslated = translation && translation !== `webCommon.footer.${translationKey}`;
            return isTranslated ? translation : this.fallbackTranslations[translationKey];
        },
    },
};
